import React from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';

type Tone = 'primary' | 'danger' | 'purple' | 'amber';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  loading?: boolean;
  /** Hide the Cancel button to use this as a single-button alert/notice. */
  hideCancel?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const toneClasses: Record<Tone, string> = {
  primary: 'bg-blue-600 hover:bg-blue-700',
  danger: 'bg-red-600 hover:bg-red-700',
  purple: 'bg-purple-600 hover:bg-purple-700',
  amber: 'bg-amber-600 hover:bg-amber-700',
};

/**
 * A small, styled yes/no confirmation dialog — a drop-in replacement for
 * window.confirm with the app's look and dark-mode support.
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  loading = false,
  hideCancel = false,
  onConfirm,
  onClose,
}) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const confirmRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!loading) onClose();
      }}
      initialFocus={hideCancel ? confirmRef : cancelRef}
      className="relative z-50"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 w-screen overflow-y-auto p-4">
        <div className="flex min-h-full items-center justify-center">
          <DialogPanel className="my-8 min-w-0 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <DialogTitle className="mb-2 break-words text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</DialogTitle>
            <div className="mb-5 break-words text-sm text-gray-600 dark:text-gray-400">{message}</div>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
              {!hideCancel && (
                <button
                  ref={cancelRef}
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="min-h-11 w-full whitespace-normal px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 disabled:opacity-50 sm:w-auto"
                >
                  {cancelLabel}
                </button>
              )}
              <button
                ref={confirmRef}
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className={`min-h-11 w-full whitespace-normal px-4 py-2 text-sm font-medium text-white rounded-md transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto ${toneClasses[tone]}`}
              >
                {loading ? 'Working…' : confirmLabel}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmModal;
